import React, { useMemo } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import PayrollViewController from "../../redux/actions/payrollApiAction";
import FormDrawer from "../../components/layout/FormDrawer";
import {
  BRAND,
  primaryButtonSx,
  inputSx,
  mobileInputSx,
} from "../../components/auth/authTheme";

import "../../styles/styles.css";
import "../../styles/layout.css";

const parseJoiningDate = (value) => {
  if (!value || value === "Not Available") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf("day") : null;
};

const getLatestJoiningDate = (employees) => {
  const dates = (employees || [])
    .map((emp) => parseJoiningDate(emp.joining_date))
    .filter(Boolean);
  if (!dates.length) return null;
  return dates.reduce((latest, current) =>
    current.isAfter(latest, "day") ? current : latest
  );
};

const PayRollFormModal = ({
  show,
  onClose,
  initialData,
  multipleData = [],
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = isMobile ? mobileInputSx : inputSx;
  const { addPayRoll } = PayrollViewController();
  const { isLoding } = useSelector((state) => state.payslip);

  const employeeSources = useMemo(
    () => (initialData ? [initialData] : multipleData || []),
    [initialData, multipleData]
  );

  const minJoinDate = useMemo(
    () => getLatestJoiningDate(employeeSources),
    [employeeSources]
  );

  const maxEffectiveDate = dayjs().startOf("day");

  const validationSchema = useMemo(
    () =>
      Yup.object({
        effective_from_date: Yup.string()
          .required("Effective from date is required")
          .test(
            "past-or-today",
            "Date must be today or in the past",
            (value) => !value || !dayjs(value).isAfter(maxEffectiveDate, "day")
          )
          .test("after-joining", function validateAfterJoining(value) {
            if (!value || !minJoinDate) return true;
            if (dayjs(value).isBefore(minJoinDate, "day")) {
              return this.createError({
                message: `Date cannot be before joining date (${minJoinDate.format("DD MMM YYYY")})`,
              });
            }
            return true;
          }),
      }),
    [minJoinDate, maxEffectiveDate]
  );

  const selectedEmployees = useMemo(() => {
    return employeeSources.map((emp) => ({
      id: emp.employee_id || emp.employee_pk || emp.id,
      name: emp.employee_name || "Unknown",
      joiningDate: parseJoiningDate(emp.joining_date),
    }));
  }, [employeeSources]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    const payload = {
      employees: selectedEmployees,
      effective_from_date: values.effective_from_date,
    };

    try {
      const data = await dispatch(addPayRoll(payload));
      if (!data) return;
      resetForm();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error generating payroll:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      key={show ? `payroll-${minJoinDate?.format("YYYY-MM-DD") || "open"}` : "closed"}
      initialValues={{ effective_from_date: "" }}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        setFieldValue,
        isSubmitting,
        submitForm,
      }) => {
        const isSaving = isSubmitting || isLoding;

        const dateHelperText =
          (touched.effective_from_date && errors.effective_from_date) ||
          (minJoinDate
            ? `Selectable from ${minJoinDate.format("DD MMM YYYY")} (joining date) to today`
            : "");

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={onClose}
              title="Add Payroll"
              subtitle="Generate salary slips for the selected employee(s)"
              width={{ xs: "100%", sm: 460, md: 480 }}
              footer={
                isMobile ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isSaving || selectedEmployees.length === 0}
                      onClick={() => submitForm()}
                      fullWidth
                      sx={{
                        ...primaryButtonSx,
                        minHeight: 48,
                        borderRadius: "12px",
                        "&.Mui-disabled": {
                          bgcolor: isSaving ? BRAND.orange : undefined,
                          color: isSaving ? "#fff" : undefined,
                          opacity: isSaving ? 0.85 : undefined,
                        },
                      }}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {isSaving ? "Generating..." : "Generate Payroll"}
                    </Button>
                    <Button
                      onClick={onClose}
                      color="inherit"
                      disabled={isSaving}
                      fullWidth
                      sx={{ minHeight: 44, textTransform: "none", fontWeight: 600 }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Button onClick={onClose} color="inherit" disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={isSaving || selectedEmployees.length === 0}
                      onClick={() => submitForm()}
                      sx={{
                        ...primaryButtonSx,
                        minWidth: 120,
                        "&.Mui-disabled": {
                          bgcolor: isSaving ? BRAND.orange : undefined,
                          color: isSaving ? "#fff" : undefined,
                          opacity: isSaving ? 0.85 : undefined,
                        },
                      }}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                    >
                      {isSaving ? "Generating..." : "Generate Payroll"}
                    </Button>
                  </>
                )
              }
            >
              <Box
                sx={{
                  bgcolor: isMobile ? "#fff" : "transparent",
                  borderRadius: isMobile ? "12px" : 0,
                  border: isMobile ? `1px solid ${BRAND.border}` : "none",
                  p: isMobile ? 1.5 : 0,
                  mb: isMobile ? 1.5 : 3,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: BRAND.navy, fontWeight: 700, mb: 1.25 }}
                >
                  Selected employees ({selectedEmployees.length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {selectedEmployees.map((emp) => (
                    <Chip
                      key={emp.id}
                      label={emp.name}
                      size="small"
                      sx={{
                        bgcolor: BRAND.bg,
                        border: `1px solid ${BRAND.border}`,
                        color: BRAND.navy,
                        fontWeight: 500,
                        maxWidth: "100%",
                        height: "auto",
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          py: 0.5,
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Effective From Date"
                  value={
                    values.effective_from_date
                      ? dayjs(values.effective_from_date)
                      : null
                  }
                  minDate={minJoinDate || undefined}
                  maxDate={maxEffectiveDate}
                  disableFuture
                  defaultCalendarMonth={
                    minJoinDate && maxEffectiveDate.isBefore(minJoinDate, "day")
                      ? minJoinDate
                      : maxEffectiveDate
                  }
                  onChange={(newValue) => {
                    setFieldValue(
                      "effective_from_date",
                      newValue ? newValue.format("YYYY-MM-DD") : ""
                    );
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      name: "effective_from_date",
                      onBlur: handleBlur,
                      error:
                        touched.effective_from_date &&
                        Boolean(errors.effective_from_date),
                      helperText: dateHelperText,
                      sx: fieldSx,
                    },
                    mobilePaper: {
                      sx: {
                        borderRadius: "16px 16px 0 0",
                      },
                    },
                    dialog: {
                      sx: {
                        "& .MuiDialog-container": {
                          alignItems: "flex-end",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <Box
                sx={{
                  mt: isMobile ? 1.5 : 3,
                  p: isMobile ? 1.25 : 2,
                  borderRadius: "10px",
                  bgcolor: BRAND.bg,
                  border: `1px solid ${BRAND.border}`,
                }}
              >
                <Typography
                  sx={{
                    color: BRAND.muted,
                    fontSize: isMobile ? "0.8125rem" : "0.875rem",
                    lineHeight: 1.5,
                  }}
                >
                  Payroll will be calculated based on attendance and salary
                  structure effective from the selected date
                  {minJoinDate
                    ? ` (on or after ${minJoinDate.format("DD MMM YYYY")})`
                    : ""}
                  .
                </Typography>
              </Box>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PayRollFormModal;
